import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import { getCountries, getStates, getCities } from '../../../redux/actions/SocietyPortal/SocietyAction/SocietyAction';
import { getVendorCategories, createVendor } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import { addVendorValidationSchema, addVendorInitialValues } from '../../../validations/SocietyPortal/VendorValidation';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import RoleSelect from '../../../components/RoleSelect';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import RcmApplicableField from './components/RcmApplicableField';
 
const AddVendor = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Get location data from Redux store
    const { countriesData, statesData, citiesData, countriesLoading, statesLoading, citiesLoading } = useSelector(state => state.SocietyReducers);

    // Get vendor categories and loading state from Redux store
    const { vendorCategoriesData, vendorCategoriesLoading, createVendorLoading } = useSelector(state => state.VendorReducers);

    // Get user data from Redux store
    const { loginData } = useSelector(state => state.loginReducer);
    const { can } = usePermissions();
    const canPickRole = can(PERMISSIONS.USER_MANAGE_ROLES);
    console.log("loginData :", loginData?.user?.user_id);
    const [documents, setDocuments] = useState({
        panCard: null,
        gstCertificate: null,
        tanCertificate: null,
        msmeCertificate: null,
    });

    // Fetch countries and vendor categories on component mount
    useEffect(() => {
        dispatch(getCountries());
        dispatch(getVendorCategories());
    }, [dispatch]);

    const handleFileChange = (e, docType) => {
        const file = e.target.files[0];
        if (file) {
            setDocuments(prev => ({ ...prev, [docType]: file }));
        }
    };

    const handleRemoveFile = (docType) => {
        setDocuments(prev => ({ ...prev, [docType]: null }));
    };

    const handleSubmit = (values) => {
        const vendorData = {
            ...values,
            documents,
            userId: loginData?.user?.user_id,
            societyId: selectedSocietyStorer.getSocietyId(),
            onSuccess: () => {
                navigate('/society-portal/vendors');
            }
        };
        // Strip role_id unless the current user is allowed to assign one.
        if (!canPickRole || !values.role_id) delete vendorData.role_id;
        dispatch(createVendor(vendorData));
    };

    // Custom handler for country change
    const handleCountryChange = (e, setFieldValue) => {
        const countryId = e.target.value;
        setFieldValue('countryId', countryId);
        setFieldValue('stateId', '');
        setFieldValue('cityId', '');
        if (countryId) {
            dispatch(getStates({ countryId }));
        }
    };

    // Custom handler for state change
    const handleStateChange = (e, setFieldValue) => {
        const stateId = e.target.value;
        setFieldValue('stateId', stateId);
        setFieldValue('cityId', '');
        if (stateId) {
            dispatch(getCities({ stateId }));
        }
    };

    // Error message component
    const ErrorMessage = ({ name, touched, errors }) => {
        if (touched[name] && errors[name]) {
            return <p className="tw-text-xs tw-text-red-500 tw-mt-1">{errors[name]}</p>;
        }
        return null;
    };

    // Input class with error state
    const getInputClass = (name, touched, errors, additionalClass = '') => {
        const baseClass = "tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none";
        const errorClass = touched[name] && errors[name]
            ? "tw-border-red-500 focus:tw-border-red-500"
            : "tw-border-gray-200 focus:tw-border-blue-500";
        return `${baseClass} ${errorClass} ${additionalClass}`;
    };

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        onClick={() => navigate('/society-portal/vendors')}
                        className="tw-p-2 tw-rounded-lg tw-bg-gray-100 hover:tw-bg-gray-200 tw-text-gray-600"
                    >
                        <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Add New Vendor</h1>
                        <p className="tw-text-xs tw-text-gray-500">Fill in the vendor details below</p>
                    </div>
                </div>
            </div>

            <Formik
                initialValues={addVendorInitialValues}
                validationSchema={addVendorValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleBlur, setFieldValue }) => (
                    <Form>
                        {/* Basic Information */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                            <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:store" className="tw-text-blue-500" />
                                Basic Information
                            </h2>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Company/Vendor Name *</label>
                                    <Field
                                        type="text"
                                        name="companyName"
                                        className={getInputClass('companyName', touched, errors)}
                                        placeholder="Enter company name"
                                    />
                                    <ErrorMessage name="companyName" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Contact Person *</label>
                                    <Field
                                        type="text"
                                        name="contactPerson"
                                        className={getInputClass('contactPerson', touched, errors)}
                                        placeholder="Enter contact person name"
                                    />
                                    <ErrorMessage name="contactPerson" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Category *</label>
                                    <Field
                                        as="select"
                                        name="category"
                                        disabled={vendorCategoriesLoading}
                                        className={getInputClass('category', touched, errors, 'tw-bg-white disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed')}
                                    >
                                        <option value="">{vendorCategoriesLoading ? 'Loading...' : 'Select category'}</option>
                                        {(vendorCategoriesData || []).map(cat => (
                                            <option key={cat.category_id} value={cat.category_id}>{cat.label}</option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="category" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Phone *</label>
                                    <Field
                                        type="tel"
                                        name="phone"
                                        className={getInputClass('phone', touched, errors)}
                                        placeholder="9876543210"
                                        maxLength="10"
                                        onKeyPress={(e) => {
                                            if (!/[0-9]/.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        onPaste={(e) => {
                                            const paste = e.clipboardData.getData('text');
                                            if (!/^\d+$/.test(paste)) {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                    <ErrorMessage name="phone" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Alternate Phone</label>
                                    <Field
                                        type="tel"
                                        name="alternatePhone"
                                        className={getInputClass('alternatePhone', touched, errors)}
                                        placeholder="9876543211"
                                        maxLength="10"
                                        onKeyPress={(e) => {
                                            if (!/[0-9]/.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        onPaste={(e) => {
                                            const paste = e.clipboardData.getData('text');
                                            if (!/^\d+$/.test(paste)) {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                    <ErrorMessage name="alternatePhone" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Email</label>
                                    <Field
                                        type="email"
                                        name="email"
                                        className={getInputClass('email', touched, errors)}
                                        placeholder="vendor@example.com"
                                    />
                                    <ErrorMessage name="email" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Website</label>
                                    <Field
                                        type="url"
                                        name="website"
                                        className={getInputClass('website', touched, errors)}
                                        placeholder="https://www.example.com"
                                    />
                                    <ErrorMessage name="website" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">
                                        Avg Bill Amount <span className="tw-text-red-500">*</span>
                                    </label>
                                    <Field
                                        type="number"
                                        name="amount"
                                        min="0"
                                        className={getInputClass('amount', touched, errors)}
                                        placeholder="Enter average bill amount"
                                    />
                                    <ErrorMessage name="amount" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Status</label>
                                    <Field
                                        as="select"
                                        name="status"
                                        className={getInputClass('status', touched, errors, 'tw-bg-white')}
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </Field>
                                </div>
                                {canPickRole && (
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Login Role</label>
                                        <RoleSelect
                                            value={values.role_id}
                                            onChange={(val) => setFieldValue('role_id', val)}
                                            placeholder="Default: Vendor"
                                            className="tw-w-full"
                                        />
                                        <p className="tw-text-[10px] tw-text-gray-400 tw-mt-0.5">Leave blank to default to Vendor</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Address */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                            <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:map-marker" className="tw-text-green-500" />
                                Address Details
                            </h2>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                                <div className="md:tw-col-span-3">
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Address</label>
                                    <Field
                                        as="textarea"
                                        name="address"
                                        rows="2"
                                        className={getInputClass('address', touched, errors, 'tw-resize-none')}
                                        placeholder="Enter full address"
                                    />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Country *</label>
                                    <select
                                        name="countryId"
                                        value={values.countryId}
                                        onChange={(e) => handleCountryChange(e, setFieldValue)}
                                        onBlur={handleBlur}
                                        disabled={countriesLoading}
                                        className={getInputClass('countryId', touched, errors, 'tw-bg-white disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed')}
                                    >
                                        <option value="">{countriesLoading ? 'Loading...' : 'Select Country'}</option>
                                        {(countriesData || []).map(country => (
                                            <option key={country.country_id} value={country.country_id}>{country.name}</option>
                                        ))}
                                    </select>
                                    <ErrorMessage name="countryId" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">State *</label>
                                    <select
                                        name="stateId"
                                        value={values.stateId}
                                        onChange={(e) => handleStateChange(e, setFieldValue)}
                                        onBlur={handleBlur}
                                        disabled={!values.countryId || statesLoading}
                                        className={getInputClass('stateId', touched, errors, 'tw-bg-white disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed')}
                                    >
                                        <option value="">{statesLoading ? 'Loading...' : 'Select State'}</option>
                                        {(statesData || []).map(state => (
                                            <option key={state.state_id} value={state.state_id}>{state.name}</option>
                                        ))}
                                    </select>
                                    <ErrorMessage name="stateId" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">City *</label>
                                    <Field
                                        as="select"
                                        name="cityId"
                                        disabled={!values.stateId || citiesLoading}
                                        className={getInputClass('cityId', touched, errors, 'tw-bg-white disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed')}
                                    >
                                        <option value="">{citiesLoading ? 'Loading...' : 'Select City'}</option>
                                        {(citiesData || []).map(city => (
                                            <option key={city.city_id} value={city.city_id}>{city.name}</option>
                                        ))}
                                    </Field>
                                    <ErrorMessage name="cityId" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Pincode</label>
                                    <Field
                                        type="text"
                                        name="pincode"
                                        maxLength="6"
                                        className={getInputClass('pincode', touched, errors)}
                                        placeholder="400001"
                                    />
                                    <ErrorMessage name="pincode" touched={touched} errors={errors} />
                                </div>
                            </div>
                        </div>

                        {/* Tax Information */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                            <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:file-document" className="tw-text-purple-500" />
                                Tax Information
                            </h2>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">PAN Number</label>
                                    <Field
                                        type="text"
                                        name="panNumber"
                                        maxLength="10"
                                        className={getInputClass('panNumber', touched, errors, 'tw-uppercase')}
                                        placeholder="ABCDE1234F"
                                    />
                                    <ErrorMessage name="panNumber" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">GST Number</label>
                                    <Field
                                        type="text"
                                        name="gstNumber"
                                        maxLength="15"
                                        className={getInputClass('gstNumber', touched, errors, 'tw-uppercase')}
                                        placeholder="22ABCDE1234F1Z5"
                                    />
                                    <ErrorMessage name="gstNumber" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">TAN Number</label>
                                    <Field
                                        type="text"
                                        name="tanNumber"
                                        maxLength="10"
                                        className={getInputClass('tanNumber', touched, errors, 'tw-uppercase')}
                                        placeholder="ABCD12345E"
                                    />
                                    <ErrorMessage name="tanNumber" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">MSME Status *</label>
                                    <Field
                                        as="select"
                                        name="msmeStatus"
                                        className={getInputClass('msmeStatus', touched, errors, 'tw-bg-white')}
                                    >
                                        <option value="">Select MSME Status</option>
                                        <option value="Micro">Micro</option>
                                        <option value="Small">Small</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Not Registered">Not Registered</option>
                                    </Field>
                                    <ErrorMessage name="msmeStatus" touched={touched} errors={errors} />
                                </div>
                                <RcmApplicableField
                                    categories={vendorCategoriesData}
                                    className={getInputClass('rcmApplicable', touched, errors, 'tw-bg-white disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed')}
                                />
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">TDS Applicable</label>
                                    <Field
                                        as="select"
                                        name="tdsApplicable"
                                        className={getInputClass('tdsApplicable', touched, errors, 'tw-bg-white')}
                                    >
                                        <option value="N">No</option>
                                        <option value="Y">Yes</option>
                                    </Field>
                                </div>
                            </div>
                        </div>

                        {/* Document Uploads */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                            <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:upload" className="tw-text-orange-500" />
                                Document Uploads
                            </h2>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                                {/* PAN Card Upload */}
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Upload PAN Card</label>
                                    {documents.panCard ? (
                                        <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50">
                                            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-truncate">
                                                <Icon icon="mdi:file-document" className="tw-text-blue-500" />
                                                <span className="tw-truncate">{documents.panCard.name}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile('panCard')}
                                                className="tw-p-1 tw-rounded hover:tw-bg-red-100 tw-text-red-500"
                                            >
                                                <Icon icon="mdi:close" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="tw-relative">
                                            <input
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={(e) => handleFileChange(e, 'panCard')}
                                                className="tw-hidden"
                                                id="panCardUpload"
                                            />
                                            <label
                                                htmlFor="panCardUpload"
                                                className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full tw-px-3 tw-py-3 tw-border-2 tw-border-dashed tw-border-gray-200 tw-rounded-lg tw-text-sm tw-text-gray-500 hover:tw-border-blue-400 hover:tw-text-blue-500 tw-cursor-pointer tw-transition-colors"
                                            >
                                                <Icon icon="mdi:cloud-upload" className="tw-text-lg" />
                                                <span>Choose File</span>
                                            </label>
                                        </div>
                                    )}
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">PDF, JPG, PNG (Max 5MB)</p>
                                </div>

                                {/* GST Certificate Upload */}
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Upload GST Certificate</label>
                                    {documents.gstCertificate ? (
                                        <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50">
                                            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-truncate">
                                                <Icon icon="mdi:file-document" className="tw-text-green-500" />
                                                <span className="tw-truncate">{documents.gstCertificate.name}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile('gstCertificate')}
                                                className="tw-p-1 tw-rounded hover:tw-bg-red-100 tw-text-red-500"
                                            >
                                                <Icon icon="mdi:close" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="tw-relative">
                                            <input
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={(e) => handleFileChange(e, 'gstCertificate')}
                                                className="tw-hidden"
                                                id="gstCertUpload"
                                            />
                                            <label
                                                htmlFor="gstCertUpload"
                                                className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full tw-px-3 tw-py-3 tw-border-2 tw-border-dashed tw-border-gray-200 tw-rounded-lg tw-text-sm tw-text-gray-500 hover:tw-border-blue-400 hover:tw-text-blue-500 tw-cursor-pointer tw-transition-colors"
                                            >
                                                <Icon icon="mdi:cloud-upload" className="tw-text-lg" />
                                                <span>Choose File</span>
                                            </label>
                                        </div>
                                    )}
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">PDF, JPG, PNG (Max 5MB)</p>
                                </div>

                                {/* TAN Certificate Upload */}
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Upload TAN Certificate</label>
                                    {documents.tanCertificate ? (
                                        <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50">
                                            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-truncate">
                                                <Icon icon="mdi:file-document" className="tw-text-purple-500" />
                                                <span className="tw-truncate">{documents.tanCertificate.name}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile('tanCertificate')}
                                                className="tw-p-1 tw-rounded hover:tw-bg-red-100 tw-text-red-500"
                                            >
                                                <Icon icon="mdi:close" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="tw-relative">
                                            <input
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={(e) => handleFileChange(e, 'tanCertificate')}
                                                className="tw-hidden"
                                                id="tanCertUpload"
                                            />
                                            <label
                                                htmlFor="tanCertUpload"
                                                className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full tw-px-3 tw-py-3 tw-border-2 tw-border-dashed tw-border-gray-200 tw-rounded-lg tw-text-sm tw-text-gray-500 hover:tw-border-blue-400 hover:tw-text-blue-500 tw-cursor-pointer tw-transition-colors"
                                            >
                                                <Icon icon="mdi:cloud-upload" className="tw-text-lg" />
                                                <span>Choose File</span>
                                            </label>
                                        </div>
                                    )}
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">PDF, JPG, PNG (Max 5MB)</p>
                                </div>

                                {/* MSME Certificate Upload */}
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Upload MSME Certificate</label>
                                    {documents.msmeCertificate ? (
                                        <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50">
                                            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-truncate">
                                                <Icon icon="mdi:file-document" className="tw-text-teal-500" />
                                                <span className="tw-truncate">{documents.msmeCertificate.name}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile('msmeCertificate')}
                                                className="tw-p-1 tw-rounded hover:tw-bg-red-100 tw-text-red-500"
                                            >
                                                <Icon icon="mdi:close" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="tw-relative">
                                            <input
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={(e) => handleFileChange(e, 'msmeCertificate')}
                                                className="tw-hidden"
                                                id="msmeCertUpload"
                                            />
                                            <label
                                                htmlFor="msmeCertUpload"
                                                className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full tw-px-3 tw-py-3 tw-border-2 tw-border-dashed tw-border-gray-200 tw-rounded-lg tw-text-sm tw-text-gray-500 hover:tw-border-blue-400 hover:tw-text-blue-500 tw-cursor-pointer tw-transition-colors"
                                            >
                                                <Icon icon="mdi:cloud-upload" className="tw-text-lg" />
                                                <span>Choose File</span>
                                            </label>
                                        </div>
                                    )}
                                    <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">PDF, JPG, PNG (Max 5MB)</p>
                                </div>
                            </div>
                        </div>

                        {/* Contract Details */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                            <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:file-sign" className="tw-text-amber-500" />
                                Contract Details
                            </h2>
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Contract Start Date</label>
                                    <Field
                                        type="date"
                                        name="contractStartDate"
                                        className={getInputClass('contractStartDate', touched, errors)}
                                    />
                                    <ErrorMessage name="contractStartDate" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Contract End Date</label>
                                    <Field
                                        type="date"
                                        name="contractEndDate"
                                        className={getInputClass('contractEndDate', touched, errors)}
                                    />
                                    <ErrorMessage name="contractEndDate" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Contract Amount</label>
                                    <Field
                                        type="number"
                                        name="contractAmount"
                                        min="0"
                                        className={getInputClass('contractAmount', touched, errors)}
                                        placeholder="0.00"
                                    />
                                    <ErrorMessage name="contractAmount" touched={touched} errors={errors} />
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Payment Terms</label>
                                    <Field
                                        as="select"
                                        name="paymentTerms"
                                        className={getInputClass('paymentTerms', touched, errors, 'tw-bg-white')}
                                    >
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                        <option value="quarterly">Quarterly</option>
                                        <option value="yearly">Yearly</option>
                                        <option value="on-completion">On Completion</option>
                                    </Field>
                                </div>
                                <div>
                                    <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1 tw-block">Contract Status</label>
                                    <Field
                                        as="select"
                                        name="contractStatus"
                                        className={getInputClass('contractStatus', touched, errors, 'tw-bg-white')}
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Expired">Expired</option>
                                    </Field>
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-mb-4">
                            <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                <Icon icon="mdi:note-text" className="tw-text-gray-500" />
                                Additional Notes
                            </h2>
                            <Field
                                as="textarea"
                                name="notes"
                                rows="3"
                                className={getInputClass('notes', touched, errors, 'tw-resize-none')}
                                placeholder="Any additional notes about this vendor..."
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button
                                type="button"
                                onClick={() => navigate('/society-portal/vendors')}
                                className="tw-px-6 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={createVendorLoading}
                                className="tw-px-6 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                <Icon icon="mdi:check" />
                                {createVendorLoading ? 'Saving...' : 'Save Vendor'}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default AddVendor;
